import { birthdayOf, detectDateOrder, formatDate, isAmbiguousSlashDate, daysUntil, displayName, turningAge, type Birthday, type Person } from '@lineage/core';

import { useState } from 'react';
import { Splash } from '../../components/Guard';
import { useTree } from '../../lib/queries';
import { PersonRow, useOpenPerson } from '../views/PersonRow';
import { compareNames, useLang, useT } from '../../i18n';
import { BackLink } from '../../components/BackLink';

interface Entry {
  person: Person;
  b: Birthday;
  days: number;
}

export function Birthdays({ treeId }: { treeId: string }) {
  const t = useT();
  const tb = t.birthdays;
  const lang = useLang();
  const MONTH_NAMES = t.common.months;
  const when = (days: number) => (days === 0 ? tb.today : days === 1 ? tb.tomorrow : tb.inDays(days));
  const q = useTree(treeId);
  const open = useOpenPerson(treeId);
  const [showPassed, setShowPassed] = useState(true);
  if (q.isLoading) return <Splash />;
  const snap = q.data;
  if (!snap) return <div className="center"><p className="muted">{t.common.couldntLoadTree}</p></div>;

  const today = new Date();
  const order = detectDateOrder(snap.people.map((p) => p.born));
  const guessed = snap.people.some((p) => isAmbiguousSlashDate(p.born));
  const entries: Entry[] = [];
  const unknown: Person[] = [];
  for (const p of snap.people) {
    const b = birthdayOf(p, order);
    if (!b) unknown.push(p);
    else if (p.living || showPassed) entries.push({ person: p, b, days: daysUntil(b, today) });
  }
  const name = (e: Entry) => displayName(e.person);
  const cmp = compareNames(lang);
  const calendar = (a: Entry, c: Entry) => a.b.day - c.b.day || cmp(name(a), name(c));
  const soon = entries.filter((e) => e.person.living && e.days <= 30).sort((a, c) => a.days - c.days || cmp(name(a), name(c)));
  const months = MONTH_NAMES.map((m, i) => ({ m, i, list: entries.filter((e) => e.b.month === i + 1).sort(calendar) }));
  const thisMonth = today.getMonth();

  const subtitle = (e: Entry) => {
    const { person: p, b } = e;
    if (!p.living) return tb.remembered;
    const turning = turningAge(b, today);
    return turning !== null ? tb.turns(turning) : tb.ageUnknown;
  };

  return (
    <div className="page">
      <div className="row" style={{ marginBottom: 16 }}>
        <BackLink fallback={{ to: '/t/$treeId', params: { treeId } }} aria-label={t.common.backToTree}>←</BackLink>
        <h1 className="grow">{tb.title}</h1>
      </div>
      <div className="stack">
        <label className="row" style={{ gap: 8 }}>
          <input type="checkbox" checked={showPassed} onChange={(e) => setShowPassed(e.target.checked)} />
          <span>{tb.includePassed}</span>
        </label>

        {entries.length === 0 && (
          <p className="muted">{tb.empty}</p>
        )}

        {soon.length > 0 && (
          <section className="stack" style={{ gap: 8 }}>
            <h2 className="section-title">{tb.comingUp} <small>{tb.next30}</small></h2>
            {soon.map((e) => (
              <PersonRow key={e.person.id} person={e.person} onOpen={open} sub={`${tb.dayMonth(e.b.day, MONTH_NAMES[e.b.month - 1]!)} · ${subtitle(e)}`} aside={when(e.days)} />
            ))}
          </section>
        )}

        {months.map(({ m, i, list }) => list.length > 0 && (
          <section key={m} className="stack" style={{ gap: 8 }} aria-label={m}>
            <h2 className="section-title" style={i === thisMonth ? { color: 'var(--brass)' } : undefined}>{m.charAt(0).toUpperCase() + m.slice(1)}<small>{i === thisMonth ? tb.thisMonth : ''}{list.length}</small></h2>
            {list.map((e) => (
              <PersonRow key={e.person.id} person={e.person} onOpen={open} dim={!e.person.living}
                sub={subtitle(e)}
                aside={<strong style={{ fontSize: 28, fontFamily: 'var(--font-serif)', fontWeight: 'var(--serif-weight)', color: 'var(--ink)' }}>{e.b.day}</strong>} />
            ))}
          </section>
        ))}

        {guessed && (
          <p className="muted" style={{ margin: 0, fontSize: 14 }}>
            {tb.dateOrder(order === 'dmy')}
          </p>
        )}

        {unknown.length > 0 && (
          <section className="stack" style={{ gap: 8 }}>
            <h2 className="section-title">{tb.noBirthday}<small>{unknown.length}</small></h2>
            <p className="muted" style={{ margin: 0 }}>{tb.noBirthdayHint}</p>
            {unknown.map((p) => <PersonRow key={p.id} person={p} onOpen={open} dim sub={p.born ? tb.born(formatDate(p.born, order, lang)) : tb.noBirthDate} />)}
          </section>
        )}
      </div>
    </div>
  );
}

